import {
  filterClientClosingPackageItems,
  type ClosingPackageItem,
  type ClosingPackageItemState,
  type ClosingPackageProjection,
} from '../types';
import {
  resolveCompositionItemDocumentLabel,
  resolveCompositionItemFileDisplayName,
  resolveDocumentTypeLabel,
  type DocumentDisplayNameSource,
} from '../../../utils/documentDisplayName';

/**
 * FE-004B — Composition table rows derived from Closing Package Projection only.
 * No Backend calls, no client-side state computation beyond filter/sort of projection fields.
 */

export type CompositionTableFilter = 'all' | 'required' | 'problems';

export type BuildCompositionTableRowsOptions = {
  /** When true, INCLUDED and EXCLUDED composition rows are returned (Editor use). */
  includeExcluded?: boolean;
};

export type CompositionTableRow = {
  rowKey: string;
  documentLabel: string;
  required: boolean;
  state: ClosingPackageItemState;
  fileName: string | null;
  documentTypeLabel: string | null;
  routeLabel: string | null;
  item: ClosingPackageItem;
};

function isIncluded(item: ClosingPackageItem): boolean {
  return item.compositionMeta?.compositionStatus !== 'EXCLUDED';
}

function isProblematic(item: ClosingPackageItem): boolean {
  return item.state === 'MISSING' || item.openTasks.length > 0;
}

function resolveRowKey(item: ClosingPackageItem): string {
  const routePart = item.compositionMeta?.routeId ?? 0;
  const slotPart = item.compositionMeta?.compositionItemId ?? item.key;
  return `${item.key}::${routePart}::${slotPart}`;
}

function resolvePrimaryDocument(item: ClosingPackageItem) {
  const linkedId = item.compositionMeta?.linkedDocumentId;
  if (linkedId != null) {
    const linked = item.documents.find((doc) => Number(doc.id) === Number(linkedId));
    if (linked) return linked;
  }
  return item.documents[0] ?? null;
}

function sortCompositionRows(rows: CompositionTableRow[]): CompositionTableRow[] {
  const hasSortOrder = rows.some((row) => row.item.compositionMeta?.sortOrder != null);
  if (!hasSortOrder) return rows;

  return [...rows].sort((a, b) => {
    const orderA = a.item.compositionMeta?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.item.compositionMeta?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.rowKey.localeCompare(b.rowKey);
  });
}

function toDocumentNameSource(
  document: ClosingPackageItem['documents'][number] | undefined
): DocumentDisplayNameSource | null {
  if (!document) return null;
  return {
    documentType: document.documentType,
    customDocumentName: document.customDocumentName,
    typeLabel: document.typeLabel,
    fileName: document.fileName,
  };
}

export function buildCompositionTableRows(
  projection: ClosingPackageProjection,
  options?: BuildCompositionTableRowsOptions
): CompositionTableRow[] {
  const rows = filterClientClosingPackageItems(projection.items ?? [])
    .filter((item) => options?.includeExcluded || isIncluded(item))
    .map((item) => {
      const document = resolvePrimaryDocument(item);
      const docSource = toDocumentNameSource(document);
      return {
        rowKey: resolveRowKey(item),
        documentLabel: resolveCompositionItemDocumentLabel(item.label, docSource),
        required: item.required,
        state: item.state,
        fileName: resolveCompositionItemFileDisplayName(docSource),
        documentTypeLabel: docSource
          ? resolveDocumentTypeLabel({
              documentType: document?.documentType,
              customDocumentName: document?.customDocumentName,
            }) || document?.typeLabel || null
          : null,
        routeLabel: document?.route?.label ?? null,
        item,
      };
    });

  return sortCompositionRows(rows);
}

export function filterCompositionTableRows<T extends CompositionTableRow>(
  rows: T[],
  filter: CompositionTableFilter
): T[] {
  if (filter === 'required') return rows.filter((row) => row.required);
  if (filter === 'problems') return rows.filter((row) => isProblematic(row.item));
  return rows;
}
